<template>
	<Container direction="column" :gap="2" :padding="0" overflow="visible">
		<Container :gap="6" :padding="0">
			<Icon name="i-globe" type="filled" state="light" size="x-small" />
			<Typography type="p3" color="light" weight="bold" overflow="ellipsis">
				{{ series.name }}
			</Typography>
		</Container>
		<Container :gap="8" :padding="0" overflow="visible">
			<Typography class="sparkline-value" type="p1" color="dark" weight="bold" overflow="ellipsis">
				246 ms
			</Typography>
			<div ref="sparkline" class="flow-sparkline" :style="styleObj" />
		</Container>
	</Container>
</template>
<script>
/* eslint-disable vue/no-unused-components */
import Typography from "./../../Typography/Typography";
import Container from "./../../Container/Container";
import Icon from "./../../Icon/Icon";
import Vue, { defineComponent } from "vue";
import SparklineTooltip from "./SparklineTooltip";

import bb, {
	area,
	areaLineRange,
	areaSpline,
	areaStep,
	bar,
	line,
	spline,
	step
} from "billboard.js";

const TYPES = {
	area: area(),
	areaLineRange: areaLineRange(),
	areaSpline: areaSpline(),
	areaStep: areaStep(),
	bar: bar(),
	line: line(),
	spline: spline(),
	step: step()
};

export default defineComponent({
	name: "Sparkline",
	components: {
		Typography,
		Container,
		Icon,
		SparklineTooltip
	},
	props: {
		type: {
			type: String,
			default: "spline",
			validator(value) {
				return [
					"area",
					"areaLineRange",
					"areaSpline",
					"areaStep",
					"bar",
					"line",
					"spline",
					"step"
				].includes(value);
			}
		},
		/**
		 * <code>
		 * <pre>
		 *
		 * 	{
		 * 		name : String  E.g. "SeriesName" | "MetricName",
		 * 		color : String E.g. "#000",
		 * 		thresholdLines:[
		 * 			{
		 * 				type : String E.g. "error" | "warning" | "success",
		 * 				value : Number E.g. Y axis value,
		 * 				text : String E.g. "> 50",
		 * 				position : String E.g. "start" | "middle" | "end"
		 * 			} , ...
		 * 		],
		 * 		points:[
		 * 				{ x : Number, y : Number}, ...
		 * 		],
		 * 		type : String  "area" | "areaLineRange" | "areaSpline" |
		 * 		"areaSplineRange" | "areaStep" | "bar" |
		 * 		"bubble" | "line" | "scatter" | "spline" | "step"
		 * 	} , ...
		 *
		 * </pre>
		 * </code>
		 */
		series: {
			type: Object,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		},
		/**
		 * vertical line to show
		 */
		eventLines: {
			type: Array,
			default: () => []
		},
		/**
		 * height of chart
		 */
		height: {
			type: Number,
			default: 20
		}
	},
	data() {
		return {
			chart: null,
			colors: [
				"var(--color1-300)",
				"var(--color2-300)",
				"var(--color3-300)",
				"var(--color4-300)",
				"var(--color5-300)",
				"var(--color6-300)"
				// "var(--color1-200)",
				// "var(--color2-200)",
				// "var(--color3-200)",
				// "var(--color4-200)",
				// "var(--color5-200)",
				// "var(--color6-200)",
				// "var(--color1-100)",
				// "var(--color2-100)",
				// "var(--color3-100)",
				// "var(--color4-100)",
				// "var(--color5-100)",
				// "var(--color6-100)"
			]
		};
	},
	computed: {
		styleObj() {
			return { height: `${this.height + 12}px` };
		}
	},
	mounted() {
		this.init();
	},
	/**
	 * register tooltip component Before mount
	 */
	beforeMount() {
		/**
		 * Register custom tolltip component
		 */
		this.tooltip = Vue.extend(SparklineTooltip);
	},
	methods: {
		init() {
			this.chart = bb.generate({
				data: {
					...this.getData()
				},
				axis: {
					y: {
						show: false,
						padding: {
							bottom: 0,
							top: 0
						}
					},
					x: {
						show: false
					}
				},
				point: {
					show: false
				},
				tooltip: {
					contents: (data, defaultTitleFormat, defaultValueFormat, color) => {
						const tooltipData = {
							title: null,
							points: []
						};
						data.forEach(d => {
							tooltipData.title = d.x;
							tooltipData.points.push({
								name: d.name,
								value: d.value,
								color: color(d),
								index: d.index
							});
						});

						const tooltipInstance = new this.tooltip({ data: tooltipData });
						return tooltipInstance.$mount().$el.outerHTML;
					},
					show: true,
					linked: true,
					grouped: true
				},
				legend: {
					show: false
				},
				grid: {
					x: {
						lines: this.eventLines
					},
					y: {
						...this.getThresholdLines()
					}
				},
				padding: {
					top: 0,
					right: 0,
					bottom: 0,
					left: 0
				},
				bindto: this.$refs.sparkline
			});
		},
		getData() {
			const columns = [];
			const colors = {};
			const types = {};
			const xAxisSet = new Set();
			const yColumn = [this.series.name];

			this.series.points.forEach(point => {
				xAxisSet.add(point.x);
				yColumn.push(point.y);
			});
			columns.push(yColumn);
			const xAxis = ["X_AXIS", ...xAxisSet];
			columns.push(xAxis);

			colors[this.series.name] = this.series.color
				? this.series.color
				: this.hashCode(this.series.name);

			if (this.series.type) {
				types[this.series.name] = TYPES[this.series.type];
			} else {
				types[this.series.name] = areaSpline();
			}

			return { x: "X_AXIS", columns, colors, types };
		},
		hashCode(str) {
			let sum = 0;
			for (const index in str) {
				sum += str.charCodeAt(index);
			}
			return this.colors[sum % this.colors.length];
		},
		/**
		 * get threshold lines if any
		 */
		getThresholdLines() {
			const lines = [];
			if (this.series.thresholdLines) {
				this.series.thresholdLines.forEach(tline => {
					lines.push({
						value: tline.value,
						text: tline.text,
						position: "end",
						class: "sparkline-threshold"
					});
				});
			}

			return { lines };
		}
	}
});
</script>
<style lang="scss">
div.flow-sparkline {
	width: 100%;
	flex: 1;
	.sparkline-threshold {
		line {
			stroke-dasharray: 4 4;
		}
	}
	svg {
		overflow: visible !important;
	}
	overflow: visible;
}

.sparkline-value {
	width: 72px;
}
</style>
